import React, {useMemo, useState} from 'react';
import type {PersonNameVariantFieldLayout} from 'components/fields/types';
import type {
  FieldError,
  NameVariantValue,
  PartNameVariant,
} from 'schemaDefinition/types';
import {assertAsNameVariantValue} from 'schemaDefinition/functions';
import {toFieldError} from 'schemaDefinition/functions/validators';
import {hasValue} from 'services/utils';
import {FieldLayout, Layout, TextField} from 'components';
import type {BaseFieldProps} from './types';
import {useDataFormContext} from '../../contexts';
import {useCustomValidator} from '../../hooks';
import {NameVariantMainForm} from './NameVariant';

type Props = BaseFieldProps & {
  part: PartNameVariant;
  isMainForm: boolean;
  /** undefined if NORAF was not loaded/found */
  isMainFormInNoraf: boolean | undefined;
  layout: PersonNameVariantFieldLayout;
};

const makeNameVariantValue = (
  id: string | null | undefined,
  name: string | null | undefined,
  surName: string | null | undefined,
  regnalNumber: string | null | undefined,
  addition: string | null | undefined,
): NameVariantValue | null => {
  if (
    ![name, surName, regnalNumber, addition].some(v => typeof v === 'string')
  ) {
    return null;
  }

  const value: NameVariantValue = {};

  if (id) {
    value.id = id;
  }

  if (typeof name === 'string') {
    value.name = name;
  }
  if (typeof surName === 'string') {
    value.surName = surName;
  }
  if (typeof regnalNumber === 'string') {
    value.regnalNumber = regnalNumber;
  }
  if (typeof addition === 'string') {
    value.addition = addition;
  }

  return value;
};

export const PersonNameVariant: React.FC<Props> = ({
  part,
  value,
  setFieldValue,
  isMainForm,
  isMainFormInNoraf,
  valuePath,
  mode,
  showWhenReadonlyAndEmpty,
  focusableId,
  layout,
}) => {
  const {showErrors} = useDataFormContext();
  const {id, name, surName, regnalNumber, addition} = useMemo(() => {
    const nameVariantValue = assertAsNameVariantValue(value, valuePath);
    return {
      id: null,
      surName: null,
      name: null,
      regnalNumber: null,
      addition: null,
      ...nameVariantValue,
    };
  }, [value, valuePath]);
  const validate = useCustomValidator(part);
  const [visited, setVisited] = useState<{
    surName?: boolean;
    name?: boolean;
  }>({});
  const error = useMemo((): FieldError => {
    if (showErrors || visited.name) {
      const valid = validate ? validate(part, value) : 'valid';
      return toFieldError(valid);
    }

    return false;
  }, [part, showErrors, validate, value, visited.name]);

  return (
    <Layout horizontal>
      <FieldLayout horizontal>
        <TextField
          type="text"
          name="surName"
          focusableId={focusableId}
          placeholder={layout.surName.placeholder}
          readonly={mode === 'read-only'}
          showWhenReadonlyAndEmpty={showWhenReadonlyAndEmpty}
          value={surName}
          layout={layout.surName.layout}
          onChange={e => {
            const newValue = e.target.value;
            setFieldValue(
              valuePath,
              makeNameVariantValue(id, name, newValue, regnalNumber, addition),
            );
          }}
          onBlur={() => setVisited(v => ({...v, surName: true}))}
          error={error}
        />
        <TextField
          type="text"
          name="name"
          focusableId={focusableId}
          placeholder={layout.name.placeholder}
          readonly={mode === 'read-only'}
          showWhenReadonlyAndEmpty={showWhenReadonlyAndEmpty}
          value={name}
          layout={layout.name.layout}
          onChange={e => {
            const newValue = e.target.value;
            setFieldValue(
              valuePath,
              makeNameVariantValue(
                id,
                newValue,
                surName,
                regnalNumber,
                addition,
              ),
            );
          }}
          onBlur={() => setVisited(v => ({...v, name: true}))}
          error={error}
        />
        {hasValue(regnalNumber) ? (
          <TextField
            type="text"
            name="regnalNumber"
            focusableId={focusableId}
            placeholder={layout.regnalNumber.placeholder}
            readonly={mode === 'read-only'}
            showWhenReadonlyAndEmpty={showWhenReadonlyAndEmpty}
            value={regnalNumber}
            layout={layout.regnalNumber.layout}
            onChange={e => {
              const newValue = e.target.value;
              setFieldValue(
                valuePath,
                makeNameVariantValue(id, name, surName, newValue, addition),
              );
            }}
          />
        ) : null}
        {hasValue(addition) ? (
          <TextField
            type="text"
            name="addition"
            focusableId={focusableId}
            placeholder={layout.addition.placeholder}
            readonly={mode === 'read-only'}
            showWhenReadonlyAndEmpty={showWhenReadonlyAndEmpty}
            value={addition}
            layout={layout.addition.layout}
            onChange={e => {
              const newValue = e.target.value;
              setFieldValue(
                valuePath,
                makeNameVariantValue(id, name, surName, regnalNumber, newValue),
              );
            }}
          />
        ) : null}

        <NameVariantMainForm
          isMainForm={isMainForm}
          isMainFormInNoraf={isMainFormInNoraf}
          sx={layout.mainForm.layout}
        />
      </FieldLayout>
    </Layout>
  );
};
